// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import moment from 'moment';

/**
 * @name - Application State
 * @description - Defines the root level application state
 * @member appSettings - Application wide settings
 * @member connections - Global list of connections available to application
 * @member recentProjects - List of recently used projects
 * @member currentProject - The active project being edited
 * @member appError - error in the app if any
 */
export interface IApplicationState {
    prebuiltSettings?: IPrebuiltSettings;
    appSettings: IAppSettings,
    connections: IConnection[],
    recentProjects: IProject[],
    currentProject: IProject,
    appError?: IAppError,
    appTitle?: string,
    showRegisterDialog?: boolean,
    showVerifyDialog?: boolean,
    showLoginDialog?: boolean,
    adHocOcr?: IAdHocProject, 
    autoLabelState?: IAutoLabelState,
    metricState?: IMetricsState,
    adHocOcrV2?: IAdHocProject, 
    adHocOcrGroup: IAdHocGroupProject,
    adHocOcrV3?: IAdHocProject,
    adHocOcrV3Group: IAdHocGroupProject
}

/**
 * @name - Application Error
 * @description - Defines error detail
 * @member title - title of the error to display
 * @member message - message of the error to display
 * @member errorCode - error category
 */
export interface IAppError {
    errorCode: ErrorCode,
    message: any,
    title?: string,
}

/**
 * Base application error
 */
export class AppError extends Error implements IAppError {
    public errorCode: ErrorCode;
    public message: string;
    public title?: string;

    constructor(errorCode: ErrorCode, message: string, title: string = null) {
        super(message);
        this.errorCode = errorCode;
        this.message = message;
        this.title = title;
    }
}

/**
 * @name - Provider Options
 * @description - Property map of key values used within a export / asset / storage provider
 */
export interface IProviderOptions {
    [key: string]: any,
}

/**
 * @name - Application settings
 * @description - Defines the root level configuration options for the application
 * @member devToolsEnabled - Whether dev tools are current open and enabled
 * @member securityTokens - Token used to encrypt sensitive project settings
 */
export interface IAppSettings {
    securityTokens: ISecurityToken[],
    thumbnailSize?: ISize,
    hideUploadingOption?: boolean;
    widgetMode?: boolean;
}

/**
 * @name - Project
 * @description - Defines the structure of a tagging project
 * @member id - Unique identifier
 * @member name - User defined name
 * @member securityToken - The Base64 encoded token used to encrypt sensitive project data
 * @member description - User defined description
 * @member tags - User defined list of tags
 * @member targetConnection - Full source connection details
 * @member assets - Map of assets within a project
 */
export interface IProject {
    id: string,
    name: string,
    version: string,
    securityToken: string,
    description?: string,
    tags: ITag[],
    sourceConnection: IConnection,
    assets?: { [index: string]: IAsset },
    lastVisitedAssetId?: string,
    apiUriBase: string,
    apiKey?: string | ISecureString,
    apiVersion?: string;
    folderPath: string,
    trainRecord: ITrainRecordProps,
    recentModelRecords: IRecentModel[],
    predictModelId: string,
    edit?: boolean,
    uploadSuccessFlag?: number,
    vendor?: string
}

export interface IGroupProject {
    projects: IProject[]
}

export interface IExportGroupProject {
    groupId: string, 
    exportDocuments: IExportDocument[]
}

export interface IExportDocument {
    requestId: string, 
    generateModel: boolean
}

export interface ITrainRecordProps {
    accuracies?: object;
    averageAccuracy?: number;
    modelInfo: {
        isComposed?: boolean;
        modelId: string;
        createdDateTime: string;
        modelName: string;
    };
}

/**
 * @name - FileInfo
 * @description - Defines the file information and content for V1 projects
 * @member content - The content of a file (JSON string)
 * @member file - The File object point to the V1 project file
 */
export interface IFileInfo {
    content: string | ArrayBuffer,
    file: File,
}

/**
 * @name - Tag
 * @description - Defines the structure of a tag
 * @member name - User defined name
 * @member color - User editable color associated to tag
 */
export interface ITag {
    name: string;
    color: string;
    type: FieldType;
    format: FieldFormat;
    documentCount?: number;
    checked?: boolean,
    labels?: ILabel[]
}

export interface ITableTag extends ITag {
    fields?: ITableField[];
    itemType?: string;
    definition?: ITableDefinition,
    visualizationHint?: TableVisualizationHint,
}

export enum TableHeaderTypeAndFormat {
    Rows = "rows",
    Columns = "columns"
}

export enum TableVisualizationHint {
    Horizontal = "horizontal",
    Vertical = "vertical",
}

/**
 * @name - Connection
 * @description - Defines a reusable data source definition for projects
 * @member id - Unique identifier for connection
 * @member name - User defined name
 * @member description - User defined short description
 * @member providerType - The underlying storage type (Local File System, Azure Blob Storage, etc)
 * @member providerOptions - Provider specific options used to connect to the data source
 */
export interface IConnection {
    id: string,
    name: string,
    description?: string,
    providerType: string,
    providerOptions: IProviderOptions | ISecureString,
}

/**
 * @name - Asset
 * @description - Defines an asset within a project
 * @member id - Unique identifier for asset
 * @member type - Type of asset (Image, Video, etc)
 * @member name - Generated name for asset
 * @member path - Relative path to asset within the underlying data source
 * @member size - Size / dimensions of asset
 * @member format - The asset format (jpg, png, mp4, etc)
 */

export interface IAsset {
    id: string;
    type: AssetType;
    state: AssetState;
    labelingState?: AssetLabelingState;
    name: string;
    path: string;
    size: ISize;
    format?: string;
    timestamp?: number;
    predicted?: boolean;
    ocr?: any;
    isRunningOCR?: boolean;
    isRunningAutoLabeling?: boolean;
    cachedImage?: string;
    mimeType?: string;
    uploadedBy?: string;
    updatedDate?: Date;
    virtualPath?: string;
    requestId?: string;
    thumbnail?: string;
    filePath?: string;
}

export interface IPrebuiltSettings {
    serviceURI: string;
    apiKey: string;
}

/**
 * @name - Asset Metadata
 * @description - Format to store asset metadata for each asset within a project
 * @member asset - References an asset within the project
 * @member regions - The list of regions drawn on the asset
 */
export interface IAssetMetadata {
    asset: IAsset,
    regions: IRegion[],
    version: string,
    labelData: ILabelData,
}

/**
 * @name - Size
 * @description - Defines the size and/or diminsion for an asset
 * @member width - The actual width of an asset
 * @member height - The actual height of an asset
 */
export interface ISize {
    width: number,
    height: number,
}

/**
 * @name - Region
 * @description - Defines a region within an asset
 * @member id - Unique identifier for this region
 * @member type - Defines the type of region
 * @member tags - Defines a list of tags applied to a region
 * @member points - Defines a list of points that define a region
 */
export interface IRegion {
    id: string,
    type: RegionType,
    category: FeatureCategory,
    tags: string[],
    points?: IPoint[],
    boundingBox?: IBoundingBox,
    value?: string,
    pageNumber: number,
    isTableRegion?: boolean,
    changed?: boolean,

}

export interface ITableRegion extends IRegion {
    rowKey: string,
    columnKey: string,
}

/**
 * @name - ILabelData
 * @description - Defines a label data correspond to an asset
 */
export interface ILabelData {
    document: string,
    labelingState?: AssetLabelingState;
    labels: ILabel[],
    tableLabels?: ITableLabel[],
    $schema?: string,
}

/**
 * @name - ILabel
 * @description - Defines a label
 */
export interface ILabel {
    id?: number,
    label: string,
    key?: IFormRegion[],
    value: IFormRegion[],
    originValue?: IFormRegion[],
    labelType?: string,
    confidence?: number,
    revised?: boolean,
}

export interface ITableLabel {
    tableKey: string,
    labels: ITableCellLabel[],
}

export interface ITableCellLabel {
    rowKey: string,
    columnKey: string,
    value: IFormRegion[],
    revised?: boolean;
}

/**
 * @name - IFormRegion
 * @description - Defines a region which consumed by FormRecognizer
 */
export interface IFormRegion {
    page: number,
    text: string,
    boundingBoxes: [number[]],
}

/**
 * @name - Bounding Box
 * @description - Defines the tag usage within a bounding box region
 * @member left - Defines the left x boundary for the start of the bounding box
 * @member top - Defines the top y boundary for the start of the boudning box
 * @member width - Defines the width of the bounding box
 * @member height - Defines the height of the bounding box
 */
export interface IBoundingBox {
    left: number,
    top: number,
    width: number,
    height: number,
}

/**
 * @name - Point
 * @description - Defines a point / coordinate within a region
 * @member x - The x value relative to the asset
 * @member y - The y value relative to the asset
 */
export interface IPoint {
    x: number,
    y: number,
}

export interface ISecureString {
    encrypted: string,
}

export interface ISecurityToken {
    name: string,
    key: string,
}

export interface IField {
    fieldKey: string;
    fieldType: FieldType;
    fieldFormat: FieldFormat;
}

export interface ITableKeyField extends IField {
    documentCount?: number;
}

export interface ITableField extends IField {
    itemType?: string;
    fields?: ITableField[];
    visualizationHint?: TableVisualizationHint;
}

export interface ITableDefinition extends IField {
    itemType?: string;
    fields?: ITableField[];
}

export interface ITableConfigItem {
    name: string,
    format: string,
    type: string;
    originalName?: string;
    originalFormat?: string,
    originalType?: string;
}

export interface IFieldInfo {
    schema?: string,
    fields: IField[],
    definitions?: any,
}

export interface IRecentModel {
    readonly composedTrainResults?: object,
    readonly accuracies?: object,
    readonly averageAccuracy?: number,
    readonly modelInfo: {
        readonly isComposed: boolean,
        readonly modelId: string,
        readonly createdDateTime: string,
        readonly modelName: string,
    },
}



export interface IHatchetAiCompanyAPIConnection {
    id: number,
    name: string,
    method: string,
    endpointUrl: string,
    authType: number,
    authEndpointUrl: string,
    credentialsJson: string,
    headersJson: string,
    bodyType: string,
    bodyTextType: string,
    bodyText: string,
    isPublic: boolean,
    fileUploadEndpoint: string
}

export interface IHatchetAiCompanyProjectTrainModel {
    id: number;
    hatchetAiCompanyProjectId: number;
    modelId: string;
    modelJson: string;
    scopeType: number;
    accuracy: number;
    uploadedBy: string;
    projectName: string;
    companyName: string;
}

export interface IRequestHeaderDetail {
    id: number,
    key: string,
    value: string
}

export interface IBodyData {
    bodyType: string,
    bodyTextType: string,
    bodyText: string
}

export interface IAuthData {
    authType: number,
    authEndpointUrl: string,
    credentialsJson: string,
}

export interface IValidationError {
    [key: string]: string[]
}


export interface IHatchetAiCompanyProjectOcr {
    id: number
    ocr: any,
    assetName: string,
    page: number
}

export interface IHatcheLabel {
    id: number
    labelData: any,
    assetName: string,
    page: number
}

export interface IOcr {
    assetName: string,
    pageNumber: number,
    ocrData: string
}

export interface IAdHocProject {
    project: IProject,
    ocrs?: IHatchetAiCompanyProjectOcr[],
    assetUrls: string[],
    email: string,
    source: string,
    labels?: IHatcheLabel[], 
    isV2: boolean,
    hasModel: boolean,
    hasRunningRequest: boolean,
    projectName?: string,
    vendor?: string,
    status?: string, 
    hideGenerateModel: boolean
}

export interface IAdHocProjectResult {
    project: string,
    ocrs?: IHatchetAiCompanyProjectOcr[],
    assetUrls: string[],
    email: string, 
    source: string,
    labels?: IHatcheLabel[],
    isV2: boolean,
    hasModel: boolean,
    hasRunningRequest: boolean,
    vendor?: string, 
    status?: string, 
    projectName?: string, 
    hideGenerateModel: boolean
}

export interface IAdHocProjectGroupResult {
    adHocProjectModels: IAdHocProjectResult[]
}

export interface IAdHocGroupProject {
    adhocGroupProjects: IAdHocProject[],
    adHocProjectProjectResults: IAdHocProjectResult[],
    generateModelGroup: Array<{ key: string; val: string }>;
    refreshKey: string;    
    projectToPosts: IProjecToPost[];
}

export interface IProjecToPost {
    projectId: string;
    fileName: string;
    generateModel: boolean;
    selected: boolean;
}

export interface IAutoLabelModel {
    modelId: string,
    isModelExist: boolean,
    documentType: string,
    countryCode: string,
    provinceCode: string,
    municipality: string,
    modelName: string,
    attachments: number,
    maxVersionLimit: number,
    modelCount: number
}

export interface IGetAutoLabelResult {
    autoLabelModels: IAutoLabelModel[]
}

export interface IAutoLabelState {
    autoLabelModels: IAutoLabelModel[]
}

export interface IGetDocumentServiceModel {
    documentServiceId: string,
    operationStatus: string,
    active: boolean
}

export interface IAutoLabelModelMetricsResultModel {
    autoLabelModels: IAutoLabelModelMetrics[],
    totalItems: number
}

export interface IMetricsV2Result {
    documentRequestMetrics: IMetricsV2[],
    totalItems: number,
    processors: string[],
    sources: string[],
    clientIds: string[],
    totalAutoLabeledDoc: number,
    totalModelRequest: number
}

export interface IAutoLabelModelMetricsOptions {
    processors: string[],
    clients: string[]
}

export interface IAutoLabelModelMetrics {
    documentServiceId: string,
    processor: string,
    clientId: string,
    documentType: string,
    autoLabelVersion: string,
    operationId: string,
    operationStatus: string,
    dateCreated: string,
    fileName: string,
    rollId: string
}

export interface IMetricsState {
    autoLabelModelMetrics: IAutoLabelModelMetrics[],
    totalAutoLabelModelMetrics: number,

    autoLabelingMetrics: IAutoLabelingMetrics[],
    totalAutoLabelingMetrics: number,

    ocrMetrics: IOcrMetrics[],
    totalOcrMetrics: number,

    processorOptions: string[],
    clientOptions: string[]

    formRecognizerModels: IFormRecognizerModel[],

    metricsV2List: IMetricsV2[],
    totalMetricsV2: number,
    processors: string[],
    sources: string[],
    clientIds: string[]
    totalAutoLabeledDoc: number, 
    totalModelRequest: number    
}

//Auto labeling metrics model
export interface IAutoLabelingMetricsResultModel {
    autoLabelingModels: IAutoLabelingMetrics[],
    totalItems: number
}
export interface IAutoLabelingMetrics {
    documentServiceId: string,
    processor: string,
    clientId: string,
    documentType: string,
    autoLabelVersion: string,
    dateCreated: string,
    fileName: string,
    rollId: string
}

export interface IOcrMetricsResultModel {
    ocrMetricsModel: IOcrMetrics[],
    totalItems: number
}

export interface IOcrMetrics {
    documentServiceId: string,
    processor: string,
    clientId: string,
    documentType: string,
    dateCreated: string,
    fileName: string,
    rollId: string
}

export interface IMetricsV2 {
    requestId: string,
    fileName: string,
    vendor: string,
    processor: string,
    source: string,
    clientId: string,
    ocrDocument: number,
    ocrPages: number,
    autoLabeledDoc: number,
    operationId: string,
    operationStatus: string,
    vendorId: string,
    version: string,
    modelRequest: number, 
    dateProcessed: Date
}

export interface IFormRecognizerModel {
    createdDateTime: Date,
    apiVersion: string,
    modelId: string, 
    expirationDateTime: Date,
    isActive: boolean
}

export interface IFormRecognizerModelList {
    value: IFormRecognizerModel[]
}


/**
 * Enum of supported error codes
 */
export enum ErrorCode {
    // Note that the value of the enum is in camelCase while
    // the enum key is in Pascal casing
    Unknown = "unknown",
    GenericRenderError = "genericRenderError",
    ProjectInvalidJson = "projectInvalidJson",
    ProjectInvalidSecurityToken = "projectInvalidSecurityToken",
    ProjectDuplicateName = "projectDuplicateName",
    SecurityTokenNotFound = "securityTokenNotFound",
    OverloadedKeyBinding = "overloadedKeyBinding",
    BlobContainerIONotFound = "blobContainerIONotFound",
    BlobContainerIOForbidden = "blobContainerIOForbidden",
    PredictWithoutTrainForbidden = "predictWithoutTrainForbidden",
    ModelNotFound = "modelNotFound",
    ModelCountLimitExceeded = "modelCountLimitExceeded",
    HttpStatusUnauthorized = "unauthorized",
    HttpStatusNotFound = "notFound",
    HttpStatusTooManyRequests = "tooManyRequests",
    RequestSendError = "requestSendError",
    ProjectUploadError = "ProjectUploadError",
}

export enum APIVersionPatches {
    patch1 = "v2.1-preview.1",
    patch2 = "v2.1-preview.2",
    patch3 = "v2.1-preview.3",
}

/**
 * @enum LOCAL - Local storage type
 * @enum CLOUD - Cloud storage type
 * @enum OTHER - Any other storage type
 */
export enum StorageType {
    Local = "local",
    Cloud = "cloud",
    Other = "other",
}

/**
 * @name - Asset Type
 * @description - Defines the type of asset within a project
 * @member Image - Specifies an asset as an image
 * @member PDF - Specifies an asset as a PDF
 * @member TIFF - Specifies an asset as a TIFF image
 */
export enum AssetType {
    Unknown = 0,
    Image = 1,
    PDF = 5,
    TIFF = 6,
}

export enum AssetMimeType {
    PDF = "application/pdf",
    TIFF = "image/tiff",
    JPG = "image/jpg",
    PNG = "image/png",
    BMP = "image/bmp",
}

/**
 * @name - Asset State
 * @description - Defines the state of the asset with regard to the tagging process
 * @member NotVisited - Specifies as asset that has not yet been visited or tagged
 * @member Visited - Specifies an asset has been visited, but not yet tagged
 * @member Tagged - Specifies an asset has been visited and tagged
 */
export enum AssetState {
    NotVisited = 0,
    Visited = 1,
    Tagged = 2,
}
/**
 * @name - Asset Labeling State
 * @description - Defines the labeling state for the asset
 * @member ManualLabeling - Specifies as an asset that has manual labeling the tags
 * @member Training - Specifies as an asset tagged data has been used for training model
 * @member AutoLabeling - Specifies as an asset that has run auto-labeling
 * @member AutoLabeledAndAdjusted -specifies as an asset that has run auto-labeling and tags manual adjusted
 */
export enum AssetLabelingState {
    ManuallyLabeled = 1,
    Trained = 2,
    AutoLabeled = 3,
    AutoLabeledAndAdjusted = 4,
    TBD = 10
}

/**
 * @name - Region Type
 * @description - Defines the region type within the asset metadata
 * @member Square - Specifies a region as a square
 * @member Rectangle - Specifies a region as a rectangle
 * @member Polygon - Specifies a region as a multi-point polygon
 */
export enum RegionType {
    Polyline = "POLYLINE",
    Point = "POINT",
    Rectangle = "RECTANGLE",
    Polygon = "POLYGON",
    Square = "SQUARE",
}

export enum EditorMode {
    Rectangle = "RECT",
    Polygon = "POLYGON",
    Polyline = "POLYLINE",
    Point = "POINT",
    Select = "SELECT",
    CopyRect = "COPYRECT",
    None = "NONE",
}

export enum FieldType {
    String = "string",
    Number = "number",
    Date = "date",
    Time = "time",
    Integer = "integer",
    SelectionMark = "selectionMark",
    Array = "array",
    Object = "object",
}

export enum LabelType {
    DrawnRegion = "region"
}

export enum TableElements {
    rows = "rows",
    row = "row",
    columns = "columns",
    column = "column",
}

export enum FieldFormat {
    NotSpecified = "not-specified",
    Currency = "currency",
    Decimal = "decimal",
    DecimalCommaSeparated = "decimal-comma-seperated",
    NoWhiteSpaces = "no-whitespaces",
    Alphanumeric = "alphanumeric",
    DMY = "dmy",
    MDY = "mdy",
    YMD = "ymd",
}

export enum FeatureCategory {
    Text = "text",
    Checkbox = "checkbox",
    Label = "label",
    DrawnRegion = "region"
}

export enum ImageMapParent {
    Predict = "predict",
    Editor = "editor",
}

export enum TagInputMode {
    Basic = "basic",
    ConfigureTable = "configureTable",
    LabelTable = "labelTable",
}

export enum AnalyzedTagsMode {
    default = "default",
    LoadingRecentModel = "loadingRecentModel",
    ViewTable = "viewTable",
}

export interface IFormRegistration {
    firstName: string,
    lastName: string,
    email: string,
    company?: number,
    title?: string,
    domain?: string
}

export interface IFormLogin {
    email: string,
    password: string,
}

export interface IRegisterUserCommand {
    form: IFormRegistration,
}

export interface IFormResetPassword {
    email: string
}

export interface IFormResetPasswordCommand {
    form: IFormResetPassword
}

export interface IFormChangePassword {
    password: string,
    confirmPassword: string
}

export interface IVerifyUserCommand {
    verificationKey: string,
}

export interface ICreateProjectCommand {
    name: string,
    description: string,
    email: string,
}

export interface IEditProjectCommand {
    id: number,
    name: string,
    description: string,
    email: string,
}


export interface ILoadProjectsCommand {
    email: string,
}

export interface ILoadProjectByNameCommand {
    email: string,
    projectName: string
}

export interface ILoginUserCommand {
    email: string,
    password: string
}

export interface ICreateSecurityTokensCommand {
    email: string,
    tokens: string
}

export interface ICreateProjectTokenCommand {
    email: string,
    securityToken: string,
    projectId: string
}

export interface IGetProjectTokenCommand {
    email: string,
    projectId: string
}

export interface ILoadProjectTokensCommand {
    email: string,
}

export interface IDeleteProjectCommand {
    id: number,
    email: string,
}

export interface IDeleteFileCommand {
    projectId: number,
    fileName: string
    email: string,
}

export interface ITagForDelete {
    name: string;
}

export interface IClearLabelDataCommand {
    projectId: number,
    fileName: string
    email: string,
    label: string
}

export interface IGetSubscriptionSummaryCommand {
    email: string,
    reCalculate: boolean
}

export interface IGetCompaniesCommand {
    isAdmin: boolean,
    reCalculate: boolean
}

export interface IValidateUserKeyCommand {
    userKey: string
}

export interface getAssetPartial {
    (assets: IAsset[], lastChunk: boolean): void;
}

export interface IProjectLabel {
    id: number,
    assetName: string,
    assetState: number,
    labelingState: number,
    assetId: number
}

export interface ICompany {
    id: number,
    name: string
}

export interface IGetCompaniesResult {
    companies: ICompany[]
}

export interface IGetDocumentServiceModel {
    documentServiceId: string,
    rollId: string,
    clientId: string, 
    processor: string, 
    dateCreated: string,
    pages: string,
    version: number
}

export interface IFilterOption {
    name: string, 
    filterType: string
}
